package com.eladfinish.bible.contest.common.usecases.getSpecificVerse

import com.eladfinish.bible.contest.common.ChaptersRepository
import com.eladfinish.bible.contest.common.usecases.VersePickTarget
import com.eladfinish.bible.contest.constants.StringConstants
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.domain.BooksRepository.bookNamesEnglish
import com.eladfinish.bible.contest.screens.search.model.VerseUiModel
import com.eladfinish.bible.contest.screens.search.usecases.PickVerseFromChapterUseCaseV2

class GetSpecificVerseV3UseCase(
    private val pickVerseFromChapterUseCaseV2: PickVerseFromChapterUseCaseV2,
    private val assetReader: AssetReader,
) {

    operator fun invoke(
        bookIndex: Int,
        chapterIndex: Int,
        verseIndex: Int,
        onlyQri: Boolean = true,
    ): VerseUiModel? {
        val chapterContent = assetReader.readFile(chapterAssetPath(bookIndex, chapterIndex))

        return pickVerseFromChapterUseCaseV2(
            bookIndex = bookIndex,
            chapterContent = chapterContent,
            target = VersePickTarget.Nth(
                verseNumber = verseIndex,
            ),
            onlyQri = onlyQri,
        )
    }

    /**
     * Warms [assetReader]'s cache for every (book, chapter) pair concurrently before a caller
     * runs many sequential [invoke] calls (e.g. root/number search results spanning dozens of
     * chapters) — a no-op on platforms where [AssetReader.readFile] is a cheap local read, but
     * avoids freezing the tab on web, where it's a blocking synchronous fetch per unique chapter.
     */
    suspend fun prefetch(bookAndChapterIndexes: List<Pair<Int, Int>>) {
        assetReader.prefetch(
            bookAndChapterIndexes.distinct().map { (bookIndex, chapterIndex) ->
                chapterAssetPath(bookIndex, chapterIndex)
            },
        )
    }

    private fun chapterAssetPath(bookIndex: Int, chapterIndex: Int): String {
        val pickedBookEng = bookNamesEnglish[bookIndex]
        val bookNumber = bookIndex + 1

        val filenameToLoad = ChaptersRepository.getFileFormat(
            number = bookNumber,
            bookName = pickedBookEng,
            chapter = ChaptersRepository.padNumber(
                bookNameEnglish = pickedBookEng,
                chapterNum = chapterIndex,
            ),
        )

        return StringConstants.ALL_CHAPTERS + assetReader.fileSeparator + filenameToLoad
    }
}
